import { TruckIcon } from 'lucide-react';
import { useState } from 'react';

import { QuoteQuotableType, QuoteRatingMethod } from '@invenco/common-domain/enums/Quote';

import { Button } from '@/components/core/button';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { extractErrorMessage } from '@/shared/helpers';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { type CreateQuoteDetails, QuoteDetailsCard } from './quote-details-card';
import {
  getInitialQuotePackages,
  QuotePackagesCard,
  type QuotePackageRow,
  toQuotePackageDTO,
} from './quote-packages-card';
import { QuoteRatesCard, type QuoteRateRow } from './quote-rates-card';

const CREATE_QUOTE_FORM_ID = 'create-quote-form';
const CREATE_QUOTE_MESSAGE_KEY = 'create-quote';

export function CreateQuote() {
  const { shippingGateway } = useGateways();
  const messages = useMessages();
  const [packages, setPackages] = useState<QuotePackageRow[]>(getInitialQuotePackages);
  const [rates, setRates] = useState<QuoteRateRow[] | undefined>();
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [areDetailsOpen, setAreDetailsOpen] = useState(true);
  const [arePackagesOpen, setArePackagesOpen] = useState(true);

  async function createQuote(details: CreateQuoteDetails) {
    const hasRequiredDetails = Boolean(
      details.accountId &&
      details.locations?.length &&
      packages.length &&
      details.to.address1 &&
      details.to.country,
    );

    if (!details.accountId || !details.locations || !hasRequiredDetails) return;

    const selectedLocations = details.locationId
      ? details.locations.filter((location) => location.id === details.locationId)
      : details.locations;
    const quotePackages = packages.map(toQuotePackageDTO);
    let completedRequests = 0;

    setAreDetailsOpen(false);
    setArePackagesOpen(false);
    setIsCreatingQuote(true);
    setRates(undefined);
    messages.loading(CREATE_QUOTE_MESSAGE_KEY, `Creating quotes (0/${selectedLocations.length})`);

    try {
      const results = await Promise.allSettled(
        selectedLocations.map(async (location) => {
          try {
            const quote = await shippingGateway.createQuote({
              quotableType: QuoteQuotableType.FULFILLMENT,
              quotableId: null,
              accountId: details.accountId,
              shipmentDate: new Date().toISOString(),
              signatureRequired: details.signatureRequired,
              from: { locationId: location.id },
              to: details.to,
              packages: quotePackages,
              parameters: {
                ratingMethod: QuoteRatingMethod.CHEAPEST,
              },
            });

            const locationRates = quote.shippingRates.map((rate, index) => ({
              ...rate,
              rowId: `${quote.id}-${rate.id}`,
              quoteId: quote.id,
              preferencePlacement: index + 1,
              fromLocationName: location.name,
              isPreferredRate: rate.id === quote.preferredShippingRate?.id,
            }));

            setRates((current) =>
              [...(current ?? []), ...locationRates].sort((a, b) => a.totalCost - b.totalCost),
            );

            return quote;
          } finally {
            completedRequests += 1;
            messages.loading(
              CREATE_QUOTE_MESSAGE_KEY,
              `Creating quotes (${completedRequests}/${selectedLocations.length})`,
            );
          }
        }),
      );

      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const firstFailure = results.find((result) => result.status === 'rejected');

      if (successCount === selectedLocations.length) {
        messages.success('Quotes created', { key: CREATE_QUOTE_MESSAGE_KEY });
      } else if (successCount > 0) {
        messages.warning(`Created ${successCount}/${selectedLocations.length} quotes`, {
          key: CREATE_QUOTE_MESSAGE_KEY,
        });
      } else {
        const errorMessage =
          firstFailure && firstFailure.status === 'rejected'
            ? extractErrorMessage(firstFailure.reason)
            : undefined;

        messages.error(`Could not create quotes: ${errorMessage || 'No quotes were created'}`, {
          key: CREATE_QUOTE_MESSAGE_KEY,
        });
      }
    } finally {
      setIsCreatingQuote(false);
    }
  }

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <TruckIcon />
        </HeaderIcon>
        <HeaderTitle>Create quote</HeaderTitle>
        <HeaderActions>
          <Button
            type="submit"
            form={CREATE_QUOTE_FORM_ID}
            disabled={packages.length === 0}
            loading={isCreatingQuote}
          >
            Create quote
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="column" className="mx-auto w-full max-w-7xl">
        <QuoteDetailsCard
          formId={CREATE_QUOTE_FORM_ID}
          open={areDetailsOpen}
          onOpenChange={setAreDetailsOpen}
          onCreateQuote={(details) => void createQuote(details)}
        />
        <QuotePackagesCard
          packages={packages}
          open={arePackagesOpen}
          onOpenChange={setArePackagesOpen}
          onChange={setPackages}
        />
        <QuoteRatesCard rates={rates} isLoading={isCreatingQuote} />
      </PageContent>
    </Page>
  );
}
